import { Injectable, inject } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { AuthStateModel, OTP_MEANS, UserModel } from "@tsin-core/models/auth.model";
import { AuthenticateAction, ExchangeCognitoTokenAction, LogoutAction, RefreshTokenAction, SetError, ValidateTokenAction, ValidateTokenActionWWU } from "@tsin-core/actions/auth.action";
import { AuthService } from "@tsin-core/services/http/auth.service";
import { catchError, switchMap, tap } from "rxjs/operators";
import { of } from "rxjs";

const noAuth = {
  authToken: null,
  token: null,
  id: null,
  email: null,
  isOtpEnabled: false,
  refreshToken: null,
  otpMeans: OTP_MEANS.NONE,
  isStaff: false,
  role: null,
  user: null,
};

@State<AuthStateModel>({
  name: 'authState',
  defaults: {
    loading: true,
    auth: noAuth,
    error: null,
  }
})

@Injectable()
export class AuthState {

  authService: AuthService = inject(AuthService);

  @Selector()
  static getToken(state: AuthStateModel): string | null {
    return state.auth.token;
  }

  @Selector()
  static getRefreshToken(state: AuthStateModel): string | null {
    return state.auth.refreshToken;
  }

  @Selector()
  static getCurrentUser(state: AuthStateModel): UserModel | null {
    return state.auth.user;
  }

  @Selector()
  static getIsAuthenticated(state: AuthStateModel): boolean {
    return !!state.auth.token;
  }

  @Selector()
  static isLoading(state: AuthStateModel) {
    return state.loading;
  }

  @Selector()
  static getError(state: AuthStateModel) {
    return state.error;
  }

  @Action(AuthenticateAction)
  validateLogin(ctx: StateContext<AuthStateModel>, action: AuthenticateAction) {
    ctx.patchState({ loading: true });
    console.log('Validate Login Started');
    return this.authService.validateAdmin(action.payload).pipe(
      tap((result: any) => {
        console.log('Validate Login Success', result);
        ctx.patchState({
          auth: result,
          loading: false
        });
      }),
      catchError((error) => {
        console.log('Validate Login Error', error);
        ctx.patchState({ loading: false, error: error.message });
        return of(error);
      })
    );
  }

  @Action(ValidateTokenAction)
  validateToken(ctx: StateContext<AuthStateModel>, action: ValidateTokenAction) {
    ctx.patchState({ loading: true });
    // console.log('Validate Token Started');
    return this.authService.validateToken(action.payload).pipe(
      tap((result: any) => {
        // console.log('Validate Token Started', result);
        ctx.patchState({
          auth: {
            id: result.id,
            email: result.email,
            token: action.payload.token,
            refreshToken: action.payload.refreshToken,
            role: result.role,
            user: result,
          },
          loading: false
        });
      }),
      catchError((error) => {
        // console.log('Validate Token Error', error);
        ctx.patchState({ loading: false, error: error.message });
        throw error;
      })
    );
  }


  @Action(ValidateTokenActionWWU)
  validateTokenWWU(ctx: StateContext<AuthStateModel>, action: ValidateTokenActionWWU) {
    ctx.patchState({ loading: true });
    return this.authService.validateTokenWWU(action.payload).pipe(
      tap((result: any) => {
        console.log('WWU', result);
        ctx.patchState({
          auth: {
            id: result.id,
            email: result.email,
            token: action.payload.token,
            refreshToken: action.payload.refreshToken,
            role: result.role,
            user: result,
          },
          loading: false
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.message });
        throw error;
      })
    );
  }

  /**
   * Exchange a Cognito ID token for an Exams-service JWT, then fetch the full
   * user profile from the Exams backend in a single chained observable.
   *
   * Échange un token Cognito ID contre un JWT Examens, puis charge le profil
   * complet depuis le backend Examens en un seul observable chaîné.
   */
  @Action(ExchangeCognitoTokenAction)
  exchangeCognitoToken(ctx: StateContext<AuthStateModel>, action: ExchangeCognitoTokenAction) {
    ctx.patchState({ loading: true });
    return this.authService.exchangeCognitoToken(action.payload.cognitoIdToken).pipe(
      switchMap((exchange) =>
        this.authService.validateToken({ token: exchange.token, refreshToken: '' }).pipe(
          tap((user: any) => {
            ctx.patchState({
              auth: {
                id: user.id,
                email: user.email,
                token: exchange.token,
                refreshToken: null,
                role: user.role,
                user,
              },
              loading: false,
            });
          }),
        ),
      ),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.message });
        throw error;
      }),
    );
  }

  @Action(RefreshTokenAction)
  refreshToken(ctx: StateContext<AuthStateModel>, action: RefreshTokenAction) {
    ctx.patchState({ loading: true });
    return this.authService.refreshToken(action.payload).pipe(
      tap((result: any) => {
        ctx.patchState({
          auth: {
            ...ctx.getState().auth,
            token: result.accessToken,
          },
          loading: false
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.message });
        throw error;
      })
    );
  }

  @Action(LogoutAction)
  logOut(ctx: StateContext<AuthStateModel>, action: LogoutAction) {
    ctx.patchState({ loading: true });
    console.log('Logout Started');
    ctx.patchState({
      auth: undefined,
      loading: false
    });
  }




}
